import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Laptop,
  ClipboardCheck,
  HandCoins,
  ThumbsUp,
  ShieldCheck,
  Clock,
  Home,
  MessageSquare,
  Ban,
  Wrench,
  Monitor,
  Cpu,
  Gamepad2,
  AlertCircle,
  CheckCircle2,
  Send,
  ArrowLeft,
  Loader2,
  Copy,
  Phone,
  Mail,
  User,
  FileText,
  Battery,
  Keyboard,
  Power
} from "lucide-react";
import SEO from "../components/SEO";
import MainLayout from "../components/MainLayout";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SellDevicePage = () => {
  const [formData, setFormData] = useState({
    full_name: "",
    phone: "",
    email: "",
    device_type: "Laptop",
    brand_model: "",
    turns_on: "Yes",
    screen_condition: "",
    keyboard_condition: "",
    battery_condition: "",
    charger_included: "Yes",
    notes: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [registrationNumber, setRegistrationNumber] = useState("");
  const [submittedData, setSubmittedData] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/device-dropoff`, formData);
      setRegistrationNumber(response.data.registration_number);
      setSubmittedData({
        name: formData.full_name,
        model: formData.brand_model,
        device_type: formData.device_type
      });
      setSubmitted(true);
      toast.success("Device submission received!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit. Please try again.");
    }
    setSubmitting(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Registration number copied!");
  };

  const steps = [
    {
      number: 1,
      icon: Laptop,
      title: "Bring your device",
      description: "Laptop, PC, mini PC, office or gaming system."
    },
    {
      number: 2,
      icon: ClipboardCheck,
      title: "Free technical evaluation",
      description: "We check performance, battery, screen, SSD, and overall condition."
    },
    {
      number: 3,
      icon: HandCoins,
      title: "Receive a final offer",
      description: "Based on the real condition of the device."
    },
    {
      number: 4,
      icon: ThumbsUp,
      title: "Instant decision",
      description: "You choose if you want to sell. No obligation."
    }
  ];

  const whyChooseReasons = [
    { icon: Clock, text: "Fast and professional evaluation" },
    { icon: Ban, text: "No waiting weeks on Marketplace" },
    { icon: Home, text: "No strangers coming to your home" },
    { icon: MessageSquare, text: "No negotiations or wasted time" },
    { icon: Wrench, text: "We accept even faulty devices" },
    { icon: ShieldCheck, text: "Safe, private, and stress-free process" }
  ];

  const whatWeAccept = [
    { icon: Laptop, text: "Laptops (working or faulty)" },
    { icon: Monitor, text: "Desktop PCs" },
    { icon: Cpu, text: "Mini PCs" },
    { icon: ShieldCheck, text: "Office systems" },
    { icon: Gamepad2, text: "Gaming systems" }
  ];

  const infoNeeded = [
    "Model",
    "Does it turn on normally",
    "Screen or keyboard issues",
    "Battery condition",
    "Charger included",
    "Any other notes"
  ];

  // Confirmation Screen
  if (submitted) {
    return (
      <MainLayout showFooter={false}>
        <div data-testid="sell-device-confirmation">
          <SEO 
            title="Submission Confirmed | Quick Laptop & PC Sale | SmartIT Aylesbury"
            description="Your device drop-off submission has been received. SmartIT will evaluate your laptop or PC and contact you with a final offer."
            url="https://thesmartit.co.uk/sell-device"
          />
          
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 rounded-2xl border border-green-500/30 p-8 text-center"
          >
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-400" />
            </div>
            
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Thank you for submitting your device to SmartIT
            </h1>
            
            <p className="text-slate-300 mb-8">
              We will complete the technical evaluation and contact you with the final offer.
            </p>

            <div className="bg-slate-900/80 rounded-xl p-6 mb-8">
              <p className="text-slate-400 text-sm mb-2">Your Registration Number</p>
              <div className="flex items-center justify-center gap-3">
                <span className="text-2xl font-mono font-bold text-blue-400" data-testid="registration-number">
                  {registrationNumber}
                </span>
                <button
                  onClick={() => copyToClipboard(registrationNumber)}
                  className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                  title="Copy registration number"
                  data-testid="copy-registration-btn"
                >
                  <Copy className="w-5 h-5 text-slate-400 hover:text-white" />
                </button>
              </div>
            </div>

            <div className="bg-slate-800/50 rounded-xl p-6 text-left mb-8">
              <h3 className="text-white font-semibold mb-4">Submission Details</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">Customer Name:</span>
                  <span className="text-white">{submittedData?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Device Type:</span>
                  <span className="text-white">{submittedData?.device_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Brand & Model:</span>
                  <span className="text-white">{submittedData?.model || "Not specified"}</span>
                </div>
              </div>
            </div>

            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-8">
              <p className="text-amber-300 text-sm font-medium">
                Please keep this number for reference.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 bg-slate-700 text-white font-semibold px-6 py-3 rounded-xl hover:bg-slate-600 transition-colors"
                data-testid="back-home-btn"
              >
                <ArrowLeft className="w-5 h-5" />
                Back to Home
              </Link>
              <a
                href="https://wa.me/447435324947?text=Hi%20SmartIT%2C%20I%20submitted%20a%20device%20for%20evaluation.%20My%20registration%20number%20is%3A%20"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-green-500 text-white font-semibold px-6 py-3 rounded-xl hover:bg-green-600 transition-colors"
                data-testid="whatsapp-followup-btn"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                Contact on WhatsApp
              </a>
            </div>
          </motion.div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div data-testid="sell-device-page">
        <SEO 
          title="Quick Laptop & PC Sale Service | Sell Your Device | SmartIT Aylesbury"
          description="Sell your laptop or PC quickly to SmartIT Aylesbury. Free technical evaluation, no waiting on Marketplace, instant offers. We accept working and faulty devices."
          keywords="sell laptop Aylesbury, sell PC Aylesbury, sell computer, buy my laptop, quick device sale, sell faulty laptop, sell gaming PC"
          url="https://thesmartit.co.uk/sell-device"
          image="https://thesmartit.co.uk/og-sell-device.png"
        />

        {/* Header */}
        <div className="bg-gradient-to-b from-slate-900 to-slate-950 border-b border-slate-800/50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Smart<span className="text-blue-500">IT</span> – Quick Laptop & PC Sale Service
            </h1>
            <p className="text-slate-300 text-lg leading-relaxed">
              Selling your laptop or PC does not have to take days or weeks. With SmartIT, you can get a fast and professional evaluation without dealing with Marketplace, time-wasters, or long conversations. Simply bring your device, and we handle the rest.
            </p>
          </motion.div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-16">
        
        {/* How It Works */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-2xl font-bold text-white mb-8">How It Works</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 rounded-2xl border border-slate-700/50 p-6"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                    <span className="text-white font-bold text-lg">{step.number}</span>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">{step.title}</h3>
                    <p className="text-slate-400 text-sm">{step.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Why Choose SmartIT */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-2xl font-bold text-white mb-6">Why Choose SmartIT?</h2>
          <div className="bg-gradient-to-br from-slate-800/40 to-slate-900/40 rounded-2xl border border-slate-700/50 p-6">
            <ul className="space-y-4">
              {whyChooseReasons.map((reason, index) => (
                <li key={index} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-green-500/10 border border-green-500/20 flex items-center justify-center flex-shrink-0">
                    <reason.icon className="w-4 h-4 text-green-400" />
                  </div>
                  <span className="text-slate-300">{reason.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.section>

        {/* What We Accept */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-2xl font-bold text-white mb-6">What We Accept</h2>
          <div className="bg-gradient-to-br from-slate-800/40 to-slate-900/40 rounded-2xl border border-slate-700/50 p-6">
            <ul className="space-y-4">
              {whatWeAccept.map((item, index) => (
                <li key={index} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-4 h-4 text-blue-400" />
                  </div>
                  <span className="text-slate-300">{item.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.section>

        {/* Information We Need */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-2xl font-bold text-white mb-4">Information We Need</h2>
          <p className="text-slate-400 mb-6">To evaluate your device properly, please provide the following details:</p>
          <div className="bg-gradient-to-br from-slate-800/40 to-slate-900/40 rounded-2xl border border-slate-700/50 p-6">
            <ul className="space-y-3">
              {infoNeeded.map((item, index) => (
                <li key={index} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-3 h-3 text-cyan-400" />
                  </div>
                  <span className="text-slate-300">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.section>

        {/* No Online Prices */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-2xl font-bold text-white mb-4">No Online Prices</h2>
          <div className="bg-gradient-to-br from-amber-500/5 to-orange-500/5 rounded-2xl border border-amber-500/20 p-6">
            <p className="text-slate-300 leading-relaxed">
              We do not estimate or publish prices online. Every device is evaluated individually based on its real condition. You will receive the final offer only after the technical check.
            </p>
          </div>
        </motion.section>

        {/* Ready to Sell */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-2xl font-bold text-white mb-4">Ready to Sell?</h2>
          <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-2xl border border-blue-500/20 p-8 mb-8">
            <p className="text-slate-300 text-lg leading-relaxed mb-4">
              You can bring your device to SmartIT anytime for a free evaluation. Quick, simple, and stress-free.
            </p>
            <p className="text-white font-bold text-lg">
              Contact SmartIT today and sell your device the easy way.
            </p>
          </div>
        </motion.section>

        {/* Device Drop-Off Form */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          id="dropoff-form"
        >
          <h2 className="text-2xl font-bold text-white mb-2">Device Drop-Off Form</h2>
          <p className="text-slate-400 mb-6">Fill in the details below to register your device for evaluation.</p>
          
          <form onSubmit={handleSubmit} className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 rounded-2xl border border-slate-700/50 p-6 sm:p-8" data-testid="device-dropoff-form">
            <div className="space-y-6">
              {/* Personal Information */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    <User className="w-4 h-4 inline mr-2" />
                    Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Your full name"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    data-testid="input-full-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    <Phone className="w-4 h-4 inline mr-2" />
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    required
                    className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Your phone number"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    data-testid="input-phone"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  <Mail className="w-4 h-4 inline mr-2" />
                  Email (optional)
                </label>
                <input
                  type="email"
                  className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="input-email"
                />
              </div>

              {/* Device Information */}
              <div className="border-t border-slate-700/50 pt-6">
                <h3 className="text-lg font-semibold text-white mb-4">Device Information</h3>
                
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      <Laptop className="w-4 h-4 inline mr-2" />
                      Device Type *
                    </label>
                    <select
                      required
                      className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={formData.device_type}
                      onChange={(e) => setFormData({ ...formData, device_type: e.target.value })}
                      data-testid="select-device-type"
                    >
                      <option value="Laptop">Laptop</option>
                      <option value="PC">PC</option>
                      <option value="Mini PC">Mini PC</option>
                      <option value="Gaming PC">Gaming PC</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      <FileText className="w-4 h-4 inline mr-2" />
                      Brand & Model *
                    </label>
                    <input
                      type="text"
                      required
                      className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="e.g. Dell XPS 15, HP Pavilion"
                      value={formData.brand_model}
                      onChange={(e) => setFormData({ ...formData, brand_model: e.target.value })}
                      data-testid="input-brand-model"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      <Power className="w-4 h-4 inline mr-2" />
                      Does it turn on? *
                    </label>
                    <select
                      required
                      className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={formData.turns_on}
                      onChange={(e) => setFormData({ ...formData, turns_on: e.target.value })}
                      data-testid="select-turns-on"
                    >
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      <Monitor className="w-4 h-4 inline mr-2" />
                      Screen Condition
                    </label>
                    <input
                      type="text"
                      className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="e.g. Good, Cracked, Dead pixels"
                      value={formData.screen_condition}
                      onChange={(e) => setFormData({ ...formData, screen_condition: e.target.value })}
                      data-testid="input-screen-condition"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      <Keyboard className="w-4 h-4 inline mr-2" />
                      Keyboard Condition
                    </label>
                    <input
                      type="text"
                      className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="e.g. Working, Missing keys, Sticky"
                      value={formData.keyboard_condition}
                      onChange={(e) => setFormData({ ...formData, keyboard_condition: e.target.value })}
                      data-testid="input-keyboard-condition"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      <Battery className="w-4 h-4 inline mr-2" />
                      Battery Condition
                    </label>
                    <input
                      type="text"
                      className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="e.g. Good, Poor, Needs replacement"
                      value={formData.battery_condition}
                      onChange={(e) => setFormData({ ...formData, battery_condition: e.target.value })}
                      data-testid="input-battery-condition"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Charger Included? *
                  </label>
                  <select
                    required
                    className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.charger_included}
                    onChange={(e) => setFormData({ ...formData, charger_included: e.target.value })}
                    data-testid="select-charger-included"
                  >
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Notes from Customer
                  </label>
                  <textarea
                    rows={3}
                    className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="Any other details about your device..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    data-testid="input-notes"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold py-4 px-6 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-dropoff-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Submit Device for Evaluation
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.section>

        {/* Contact CTA */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-2xl border border-green-500/20 p-8 text-center">
            <h3 className="text-xl font-bold text-white mb-4">Prefer to talk directly?</h3>
            <p className="text-slate-300 mb-6">Contact us on WhatsApp for a quick conversation about your device.</p>
            <a
              href="https://wa.me/447435324947?text=Hi%20SmartIT%2C%20I%20would%20like%20to%20sell%20my%20device."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-green-500 text-white font-semibold px-8 py-3 rounded-xl hover:bg-green-600 transition-colors"
              data-testid="sell-whatsapp-btn"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              Contact on WhatsApp
            </a>
          </div>
        </motion.section>
        </div>
      </div>
    </MainLayout>
  );
};

export default SellDevicePage;
